import CurrencyIcon from '@/components/common/CurrencyIcon';
import Divider from '@/components/common/Divider';
import Tooltip from '@/components/common/Tooltip';
import { shouldUseNopBalance } from '@/helpers/environmentHelper';
import { useAppSelector } from '@/state/hooks';
import {
    BalanceItem,
    CollapsedBalances,
    selectAggregatedBalance,
    selectNopBalance
} from '@/state/reducers/balanceSlice';
import { LumeFxSide } from '@/types/lumeFx';
import { Currency, FeePreviewResponse } from '@/types/sso';
import { getCurrency, UseCurrencyReturn } from '@/utils/hooks/useCurrency';
import useLanguage from '@/utils/hooks/useLanguage';
import { UseOrderExecutionReturn } from '@/utils/hooks/useOrderExecution';
import { UseTotalsReturn } from '@/utils/hooks/useTotals';
import { useSso } from '@/utils/providers/SSOProvider';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { Fragment, useMemo } from 'react';
import { useNumberFormatter } from 'react-aria';
import { LuArrowRight, LuTicket } from 'react-icons/lu';

interface BalancePreviewProps extends UseOrderExecutionReturn {
    parentSize: number;
    quantity: number | null;
    fees?: FeePreviewResponse[];
    totals: UseTotalsReturn;
}

interface FeeSubRow {
    isFee: boolean;
    delta: number;
}

interface FeeImpact {
    ccyConfig: UseCurrencyReturn;
    delta: number;
    feesApplied?: boolean; // To indicate if the fee details have been combined with totals or quantity
    subRows: FeeSubRow[];
}

const feeToImpact = (fee: FeePreviewResponse, instrumentCurrenciesData: Currency[]) => {
    const currency = instrumentCurrenciesData?.find((c) => c.id === fee?.feeCurrencyId);
    const ccyConfig = getCurrency(currency?.assetName || currency?.code || '', fee.feeCurrencyId);

    return {
        ccyConfig,
        delta: BigNumber(fee.calculatedFeeAmount).negated().toNumber(),
        feesApplied: true
    };
};

export function BalancePreview(props: BalancePreviewProps) {
    const { parentSize, quantity, fees, totals, ...orderExecutionProps } = props;

    const { sideState, isCcy2Order, ccy1Config, ccy2Config, activeCurrencyConfig } = orderExecutionProps;

    const lang = useLanguage();

    const [side, setSide] = sideState;

    const { instrumentCurrenciesApi } = useSso();
    const { data: instrumentCurrenciesData = [] } = instrumentCurrenciesApi;

    const nopBalance = useAppSelector(selectNopBalance);
    const celerBalance = useAppSelector(selectAggregatedBalance);

    const isNarrowScreen = useMemo(() => parentSize < 500, [parentSize]);

    const balances = useMemo(() => {
        return shouldUseNopBalance ? nopBalance?.balances || [] : celerBalance;
    }, [nopBalance, celerBalance]);

    const combinedTradeImpact = useMemo(() => {
        // To find out if totals has same currency as any of the fees. If so,
        // we combine those details to avoid showing same currency twice in the balance preview.
        if (fees) {
            const modifiedFees = fees.map((fee) => feeToImpact(fee, instrumentCurrenciesData)) as FeeImpact[];

            const commonTotalFactor = modifiedFees.findIndex((fee) => fee.ccyConfig.id === totals.ccyConfig.id);
            const commonQuantityFactor = modifiedFees.findIndex(
                (fee) => fee.ccyConfig.id === (activeCurrencyConfig?.id || '')
            );

            if (commonTotalFactor !== -1) {
                const feeDetails = modifiedFees[commonTotalFactor];
                const totalImpact = BigNumber(totals.totalValue);

                let signedImpact = totalImpact;
                if (side === LumeFxSide.BUY) signedImpact = totalImpact.negated();

                const combinedImpact = signedImpact.plus(BigNumber(feeDetails.delta));

                modifiedFees[commonTotalFactor] = {
                    ...modifiedFees[commonTotalFactor],
                    delta: combinedImpact.toNumber(),
                    subRows: [
                        {
                            isFee: true,
                            delta: feeDetails.delta
                        },
                        {
                            isFee: false,
                            delta: signedImpact.toNumber()
                        }
                    ]
                };
            } else {
                const signedImpact =
                    side === LumeFxSide.BUY ? BigNumber(totals.totalValue).negated().toNumber() : totals.totalValue;
                modifiedFees.push({
                    ccyConfig: totals.ccyConfig,
                    delta: signedImpact,
                    subRows: [
                        {
                            isFee: false,
                            delta: signedImpact
                        }
                    ]
                });
            }

            if (commonQuantityFactor !== -1) {
                const feeDetails = fees[commonQuantityFactor];
                const feeImpact = BigNumber(feeDetails.calculatedFeeAmount);
                const quantityImpact = BigNumber(quantity || 0);

                let signedImpact = quantityImpact;
                if (side === LumeFxSide.SELL) signedImpact = quantityImpact.negated();

                const combinedImpact = feeImpact.negated().plus(signedImpact);

                modifiedFees[commonQuantityFactor] = {
                    ...modifiedFees[commonQuantityFactor],
                    delta: combinedImpact.toNumber(),
                    subRows: [
                        {
                            isFee: true,
                            delta: feeImpact.negated().toNumber()
                        },
                        {
                            isFee: false,
                            delta: signedImpact.toNumber()
                        }
                    ]
                };
            } else {
                const signedImpact =
                    side === LumeFxSide.BUY
                        ? quantity || 0
                        : BigNumber(quantity || 0)
                              .negated()
                              .toNumber();

                modifiedFees.push({
                    ccyConfig: isCcy2Order ? ccy2Config : ccy1Config,
                    delta: signedImpact,
                    subRows: [
                        {
                            isFee: false,
                            delta: signedImpact
                        }
                    ]
                });
            }

            return modifiedFees;
        } else {
            const totalImpact = BigNumber(totals.totalValue);
            return [
                {
                    ccyConfig: isCcy2Order ? ccy2Config : ccy1Config,
                    delta:
                        side === LumeFxSide.BUY
                            ? quantity || 0
                            : BigNumber(quantity || 0)
                                  .negated()
                                  .toNumber()
                },
                {
                    ccyConfig: totals.ccyConfig,
                    delta: side === LumeFxSide.BUY ? totalImpact.negated().toNumber() : totalImpact.toNumber()
                }
            ];
        }
    }, [fees, totals, instrumentCurrenciesData]);

    return (
        <div
            className={cn('bg-brand-background-dark border border-brand-neutral-700 rounded-md p-3 overflow-x-clip', {
                'pb-1': !isNarrowScreen
            })}>
            <div className="flex text-brand-text text-xs">
                <div className="w-32">
                    {lang.balances.currency}/{lang.portal.assets}
                </div>
                {!isNarrowScreen && <div className="flex-1 basis-0 text-right">{lang.orderForm.estimatedBalances}</div>}
                <div
                    className={cn('text-right', {
                        'w-32': !isNarrowScreen,
                        'flex-1 basis-0': isNarrowScreen
                    })}>
                    {lang.orderForm.estimatedProceeds}
                </div>
            </div>
            {combinedTradeImpact.map((impact, id) => {
                return (
                    <Fragment key={id}>
                        {shouldUseNopBalance ? (
                            <NopBalanceRow
                                impact={impact}
                                balances={balances as CollapsedBalances[]}
                                isNarrowScreen={isNarrowScreen}
                            />
                        ) : (
                            <OldBalanceRow
                                impact={impact}
                                balances={balances as BalanceItem[]}
                                isNarrowScreen={isNarrowScreen}
                            />
                        )}
                        {((id !== combinedTradeImpact.length - 1 && !isNarrowScreen) || isNarrowScreen) && <Divider />}
                    </Fragment>
                );
            })}
            {isNarrowScreen && (
                <div className="text-brand-neutral-400 text-xs italic mt-2">*{lang.orderForm.estimatedBalances}</div>
            )}
        </div>
    );
}

interface NopBalanceRowProps {
    impact: FeeImpact;
    balances: CollapsedBalances[];
    isNarrowScreen: boolean;
}

const NopBalanceRow = (props: NopBalanceRowProps) => {
    const { impact, balances, isNarrowScreen } = props;

    const { delta, ccyConfig } = impact;

    const balance = useMemo(() => {
        return balances.find((b) => b.currency === ccyConfig?.show);
    }, [balances, ccyConfig]);

    const oldVal = balance?.totalAmount || 0;
    const newVal = useMemo(() => {
        return BigNumber(balance?.totalAmount || 0)
            .plus(delta)
            .toNumber();
    }, [delta, balance]);

    return <BalanceRow {...impact} oldVal={oldVal} newVal={newVal} isNarrowScreen={isNarrowScreen} />;
};

interface OldBalanceRowProps {
    impact: FeeImpact;
    balances: BalanceItem[];
    isNarrowScreen: boolean;
}

const OldBalanceRow = (props: OldBalanceRowProps) => {
    const { impact, balances, isNarrowScreen } = props;

    const { delta, ccyConfig } = impact;

    const balance = useMemo(() => {
        return balances.find((b) => b.currency === ccyConfig?.celer);
    }, [balances, ccyConfig]);

    const oldVal = balance?.netPosition || 0;
    const newVal = useMemo(() => {
        return BigNumber(balance?.netPosition || 0)
            .plus(delta)
            .toNumber();
    }, [delta, balance]);

    return <BalanceRow {...impact} oldVal={oldVal} newVal={newVal} isNarrowScreen={isNarrowScreen} />;
};

interface BalanceRowProps {
    delta: number;
    oldVal: number;
    newVal: number;
    subRows: FeeSubRow[];
    ccyConfig: UseCurrencyReturn;
    feesApplied?: boolean;
    isNarrowScreen: boolean;
}

const BalanceRow = (props: BalanceRowProps) => {
    const { delta, oldVal, newVal, subRows, ccyConfig, feesApplied, isNarrowScreen } = props;

    const lang = useLanguage();

    const { order_decimals } = ccyConfig;

    const formatQty = useNumberFormatter({
        minimumFractionDigits: 0,
        maximumFractionDigits: order_decimals
    });

    const { isPositive, isNegative } = useMemo(() => {
        return {
            isPositive: delta > 0,
            isNegative: delta < 0
        };
    }, [delta]);

    return (
        <div className="relative flex justify-between text-sm py-2 gap-5">
            <div
                className={cn('flex flex-1 basis-0 flex-wrap w-full justify-between gap-2', {
                    'pr-32': !isNarrowScreen
                })}>
                <div className="flex items-center gap-1.5 w-32">
                    <CurrencyIcon ccy={ccyConfig?.show || ''} className="w-4 h-4" />
                    <span className="text-brand-text text-sm">{ccyConfig?.show}</span>
                    {feesApplied && (
                        <Tooltip content={lang.orderForm.feesApplied} placement="top-start">
                            <LuTicket className="w-3.5 h-3.5 text-brand-green-light" />
                        </Tooltip>
                    )}
                </div>
                <div
                    className={cn('flex items-center gap-2', {
                        'justify-end': !isNarrowScreen,
                        'justify-start w-full italic': isNarrowScreen
                    })}>
                    <div className="relative text-brand-neutral-400">{formatQty.format(oldVal)}</div>
                    <div className="w-3.5 h-3.5">
                        <LuArrowRight className="w-3.5 h-3.5 text-brand-text" />
                    </div>
                    <div className="relative text-brand-text">
                        {formatQty.format(newVal)}
                        {isNarrowScreen && '*'}
                    </div>
                </div>
            </div>
            <div
                className={cn(`w-32 text-right absolute right-0`, {
                    'text-brand-green': isPositive,
                    'text-brand-red': isNegative,
                    'text-brand-neutral-500': !isPositive && !isNegative
                })}>
                {delta > 0 ? '+' : ''}
                {formatQty.format(delta)}
                {` ${ccyConfig.show}`}
            </div>
        </div>
    );
};
